import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import * as PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  Modal, Form, Input, Radio, Table, Typography,
} from 'antd';
import ViaValidationAlert from '../../../../components/fields/ViaValidationAlert';
import {
  selectEditState, selectSubmitChecklistCategoryRequest, selectChecklistCategoryReferencesRequest,
} from '../../../../state/ui/configuration/inspection/checklistCategoryUiReducer';
import { selectChecklistCategory } from '../../../../state/domain/checklistCategories/checklistCategoriesReducer';
import {
  cancelEditChecklistCategory, requestAddChecklistCategory, requestUpdateChecklistCategory, requestForChecklistCategoryReferences,
} from '../../../../state/ui/configuration/inspection/checklistCategoryUiActions';
import { codeAvailabilityActions } from './config-util';
import { selectChecklistCategoryReferences } from '../../../../state/domain/checklistCategories/checklistCategoryReferencesReducer';


const { Column } = Table;
const { Text } = Typography;

/**
 * The field for the description label. Allows clear, and max length set. Required plus extra duplicate validation
 * The rules are injected into the form using the form field decorator.
 */
const DescriptionField = injectIntl(({
  form, checklistCategory, intl, checkForDuplicates,
}) => (
  <Form.Item required label={<FormattedMessage id="description" />}>
    { form.getFieldDecorator('categoryDescription', {
      initialValue: checklistCategory ? checklistCategory.categoryDescription : undefined,
      rules: [
        { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
        {
          validator(rule, value, callback) {
            if (value !== (checklistCategory ? checklistCategory.categoryDescription : undefined)
              && checkForDuplicates(checklistCategory ? checklistCategory.categoryCode : null, value)) {
              callback(intl.formatMessage({ id: 'Duplicate' }));
            }
            callback();
          },
        },
      ],
    })(
      <Input
        type="text"
        maxLength={250}
        allowClear
        placeholder={intl.formatMessage({ id: 'enter' })}
      />,
    )}
  </Form.Item>
));

/**
 * The field for the publish label.  Toggle that also controls mandatory flag on checklist.
 * The rules are injected into the form using the form field decorator.
 */
const PublishField = injectIntl(({ form, checklistCategory, intl }) => (
  <Form.Item label={<FormattedMessage id="published" />}>
    { form.getFieldDecorator('active', {
      initialValue: checklistCategory ? checklistCategory.active : false,
      rules: [
        { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
      ],
    })(
      <Radio.Group buttonStyle="solid">
        <Radio.Button value={codeAvailabilityActions.PUBLISH}><FormattedMessage id="published" /></Radio.Button>
        <Radio.Button value={codeAvailabilityActions.UNPUBLISH}><FormattedMessage id="notPublished" /></Radio.Button>
      </Radio.Group>,
    )}
  </Form.Item>
));

function ChecklistCategoryReferencesTable({
  checklistCategoryReferences,
}) {
  const RowTotal = () => (
    <div>
      <Text>{`${checklistCategoryReferences.length} `}</Text>
      <FormattedMessage id="items" />
    </div>
  );

  return (
    <Form.Item label={
      (
        <div>
          <FormattedMessage id="references1" />
          <FormattedMessage id="checklists" />
        </div>
    )}
    >
      <Table
        rowKey={checklistCategoryRefs => checklistCategoryRefs.checkListCode}
        dataSource={checklistCategoryReferences}
        size="small"
        showHeader={false}
        bordered
        pagination={{ defaultPageSize: 8, showTotal: () => <RowTotal /> }}
      >
        <Column
          dataIndex="description"
          key="description"
        />
      </Table>
    </Form.Item>
  );
}

/**
 * Validation for the props on the component.
 */
ChecklistCategoryReferencesTable.propTypes = {
  checklistCategoryReferences: PropTypes.arrayOf(
    PropTypes.shape({
      checklistItemDescription: PropTypes.string,
    }),
  ),
};

/**
 * Default data for the component if nothing has been supplied.
 * Only needed for non-required props.
 */
ChecklistCategoryReferencesTable.defaultProps = {
  checklistCategoryReferences: null,
};

const EditChecklistCategory = injectIntl(Form.create({ name: 'edit-checklist-category' })(({
  intl, form, visible, checklistCategory, onCreate, onUpdate, onCancel, checklistCategoriesLoading, checklistCategories,
  isSubmitting, validationBox, descValidator, checklistCategoryReferences, loadChecklistCategoryReferences, ...rest
}) => {
  const isCreating = !checklistCategory;
  const titleId = isCreating ? 'addNewCategory' : 'editCategory';
  const buttonId = 'save';

  const { resetFields } = form;

  // Reset fields when it becomes visible.
  useEffect(() => {
    if (visible) {
      if (!isCreating) loadChecklistCategoryReferences(checklistCategory);
      resetFields();
    }
  }, [resetFields, visible, checklistCategory, loadChecklistCategoryReferences, isCreating]);

  // On form submit.
  const submitChecklistCategory = useCallback(() => {
    form.validateFields((err, values) => {
      if (err) return; // Nothing more to do.

      if (isCreating) {
        onCreate({ ...values });
      } else {
        const updatedChecklistCategory = {
          ...checklistCategory,
          ...values,
        };
        onUpdate(updatedChecklistCategory);
      }
    });
  }, [form, checklistCategory, isCreating, onCreate, onUpdate]);

  return (
    <Modal
      title={<FormattedMessage id={titleId} />}
      okText={<FormattedMessage id={buttonId} />}
      visible={visible}
      onOk={submitChecklistCategory}
      onCancel={onCancel}
      confirmLoading={isSubmitting}
      {...rest}
    >
      <Form layout="vertical" onSubmit={(e) => { e.preventDefault(); submitChecklistCategory(); }}>
        <ViaValidationAlert validationError={validationBox} />
        <DescriptionField form={form} checklistCategory={checklistCategory} checkForDuplicates={descValidator} />
        <PublishField form={form} checklistCategory={checklistCategory} />
        {
          !isCreating ? <ChecklistCategoryReferencesTable checklistCategoryReferences={checklistCategoryReferences} /> : undefined
        }
      </Form>
    </Modal>
  );
}));

const mapStateToProps = (state) => {
  const editState = selectEditState(state);
  const submitRequest = selectSubmitChecklistCategoryRequest(state);
  const checklistCategoryReferencesRequest = selectChecklistCategoryReferencesRequest(state);
  const checklistCategoryReferences = selectChecklistCategoryReferences(state);

  return {
    visible: editState.isEditing,
    checklistCategory: editState.categoryCode ? selectChecklistCategory(state, editState.categoryCode) : null,
    isSubmitting: submitRequest ? submitRequest.isLoading : false,
    validationBox: submitRequest ? submitRequest.error : undefined,
    checklistCategoryReferencesIsLoading: checklistCategoryReferencesRequest ? checklistCategoryReferencesRequest.isLoading : false,
    checklistCategoryReferences: checklistCategoryReferences || [],
  };
};

const mapDispatchToProps = dispatch => ({
  onCreate: (checklistCategory, newChecklistCategories) => dispatch(requestAddChecklistCategory(checklistCategory, newChecklistCategories)),
  onUpdate: (checklistCategory, newChecklistCategories) => dispatch(requestUpdateChecklistCategory(checklistCategory, newChecklistCategories)),
  onCancel: () => dispatch(cancelEditChecklistCategory()),
  loadChecklistCategoryReferences:
  checklistCategory => dispatch(requestForChecklistCategoryReferences(checklistCategory ? checklistCategory.categoryCode : null)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditChecklistCategory);
